import type {
  CalculatorActionType,
  CalculatorCashFlow,
  CalculatorEntry,
  CalculatorEntryStatus,
  CalculatorRestDirection,
} from './types'

const PAYMENT_ACTIONS = new Set<CalculatorActionType>([
  'betslip_payin',
  'voucher_create',
  'direct_deposit',
])
const CANCELLATION_ACTIONS = new Set<CalculatorActionType>([
  'betslip_cancel',
  'bet_cancel',
  'voucher_revoke',
])
const PAYOUT_ACTIONS = new Set<CalculatorActionType>([
  'betslip_payout',
  'betslip_cashout',
  'bet_payout',
  'bet_cashout',
  'voucher_redeem',
  'retail_location_withdrawal',
])

const roundAmount = (value: number) => Math.round(value * 100) / 100

const isIncludedInCalculation = (entry: CalculatorEntry) =>
  entry.included && entry.status === 'success'

export const calculatePayments = (entries: CalculatorEntry[]) =>
  roundAmount(
    entries.filter(isIncludedInCalculation).reduce((sum, entry) => {
      const amount = Math.abs(entry.amount)

      if (PAYMENT_ACTIONS.has(entry.action)) {
        return sum + amount
      }

      if (CANCELLATION_ACTIONS.has(entry.action)) {
        return sum - amount
      }

      return sum
    }, 0),
  )

export const calculatePayouts = (entries: CalculatorEntry[]) =>
  roundAmount(
    entries.filter(isIncludedInCalculation).reduce((sum, entry) => {
      if (!PAYOUT_ACTIONS.has(entry.action)) {
        return sum
      }

      return sum + Math.abs(entry.amount)
    }, 0),
  )

export const calculateRest = (moneyIn: number, payouts: number, payments: number) =>
  roundAmount(moneyIn + payouts - payments)

export const getRestDirection = (rest: number): CalculatorRestDirection => {
  if (rest === 0) {
    return 'balanced'
  }

  return rest > 0 ? 'give_to_player' : 'collect_from_player'
}

export const getDisplayRest = (rest: number) => roundAmount(Math.abs(rest))

export const calculateCashFlow = (
  moneyIn: number,
  entries: CalculatorEntry[],
): CalculatorCashFlow => {
  const payments = calculatePayments(entries)
  const payouts = calculatePayouts(entries)
  const rest = calculateRest(moneyIn, payouts, payments)

  return {
    moneyIn: roundAmount(moneyIn),
    payments,
    payouts,
    rest,
    restDirection: getRestDirection(rest),
    displayRest: getDisplayRest(rest),
  }
}

export const countExcludedEntries = (entries: CalculatorEntry[]) =>
  entries.filter((entry) => !entry.included && entry.status === 'success').length

export const isSuccessfulEntry = (status: CalculatorEntryStatus) => status === 'success'
